import { ContextsService, type ContextDto } from '@inference.engine/api-client/web';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { inferenceHttp } from '@/lib/api/http-client';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';

/**
 * Probes `GET /contexts/:contextId` to decide whether the current user can
 * open the originating chat session for a library artifact. The endpoint is
 * the source of truth — a 200 means the user can navigate, anything else
 * (403, 404, 5xx, network failure) collapses to "no access" so api outages
 * cannot break the preview modal.
 *
 * Cached under `['context-access', contextId]` so the same context returns
 * the same answer across projects without an extra round trip.
 * `staleTime: Infinity` because ownership doesn't flip during normal usage;
 * `retry: false` because 403/404 are correct hide signals, not transient.
 */
export function useChatSessionAccess(contextId: string | null): UseQueryResult<ContextDto, Error> {
  return useQuery({
    queryKey: ['context-access', contextId],
    queryFn: async () => {
      // `enabled: contextId !== null` keeps this branch unreachable when
      // null; the explicit guard makes the invariant load-bearing for TS
      // (no `as string` cast) and surfaces a clear error if the gate ever
      // breaks instead of silently issuing a request with `undefined`.
      if (contextId === null) {
        throw new Error('useChatSessionAccess: queryFn invoked with null contextId');
      }
      // Suppress the global problem-details toast: a 403 here is the expected
      // signal that the user can't open the originating session, not a real
      // error to surface. See `problem-details.interceptor.ts`.
      const response = await ContextsService.getContext({
        client: inferenceHttp,
        path: { contextId },
        // eslint-disable-next-line i18next/no-literal-string -- HTTP header value
        headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
      });

      if (!response.data) {
        throw new Error('Failed to probe chat session access');
      }

      return response.data;
    },
    enabled: contextId !== null,
    staleTime: Infinity,
    retry: false,
  });
}
